import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import FilterPopup from './FilterPopup';
import ReservationPopup from './ReservationPopup';
import WaitlistConfirmationPopup from './WaitlistConfirmationPopup';
import { ReactComponent as FilterIcon } from '../../assets/filter-icon.svg';
import { classService, locationService } from '../../api/services.js';
import { instructors } from '../../../config/instructors';

const Container = styled.div`
  background: ${props => props.theme.colors.primary.background};
  min-height: 100vh;
  padding-bottom: ${props => props.theme.spacing.xl};
`;

const FilterButton = styled.button`
  background: ${props => props.theme.colors.primary.background};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  color: ${props => props.theme.colors.primary.buttons};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${props => props.theme.transitions.default};
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    right: 5px;
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
  }
  
  &:active {
    transform: translateY(-50%);
  }
  
  /* Show asterisk when filters are active */
  ${props => props.$hasActiveFilters && `
    &::after {
      content: '*';
      position: absolute;
      top: -2px;
      right: -2px;
      font-size: 16px;
      font-weight: bold;
      color: ${props.theme.colors.accent.main};
      z-index: 11;
    }
  `}
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const CalendarContainer = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.layoutGutter} ${props => props.theme.spacing.xs};
  background: ${props => props.theme.colors.primary.background};
  border-bottom: 1px solid ${props => props.theme.colors.neutral.light};
  max-width: 1200px;
  margin: 0 auto;
  overflow: visible;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xs};
  }
`;

const CalendarNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.sm};
  padding: 0 60px;
  width: 800px;
  margin: 0 auto ${props => props.theme.spacing.sm} auto;
  box-sizing: border-box;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 45px;
    width: 100%;
  }
`;

const MonthDisplay = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  text-align: center;
  white-space: nowrap;
  width: 100%;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1rem;
  }
`;

const CalendarHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
  width: 100%;
  position: relative;
  min-height: 36px;
  padding-top: ${props => props.theme.spacing.xs};
`;

const ReturnToTodayButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.accent.main};
  border-radius: 20px;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  cursor: pointer;
  color: ${props => props.theme.colors.accent.main};
  font-size: 0.8rem;
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  transition: ${props => props.theme.transitions.default};
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  position: absolute;
  left: 70px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    left: 5px;
  }
  
  &:hover {
    background: ${props => props.theme.colors.accent.main};
    color: ${props => props.theme.colors.primary.background};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 0.7rem;
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.xs};
  }
`;

const CalendarNavButton = styled.button`
  background: ${props => props.theme.colors.primary.background};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  color: ${props => props.theme.colors.primary.buttons};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  transition: ${props => props.theme.transitions.default};
  flex-shrink: 0;
  position: absolute;
  z-index: 2;
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.theme.shadows.medium};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: ${props => props.theme.colors.primary.background};
      color: ${props => props.theme.colors.primary.buttons};
      transform: none;
      box-shadow: none;
    }
  }
  
  &:first-child {
    left: 85px;
  }
  
  &:last-child {
    right: 85px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    font-size: 1.2rem;
    
    &:first-child {
      left: 0;
    }
    
    &:last-child {
      right: 0;
    }
  }
`;

const CalendarScroll = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  justify-content: center;
  padding: ${props => props.theme.spacing.xs} 40px;
  width: 100%;
  overflow: visible;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: 2px;
    padding: 0;
    justify-content: space-between;
    overflow-x: auto;
    overflow-y: visible;
    
    /* Hide scrollbar on mobile */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const DateOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  background: ${props => props.$selected ? props.theme.colors.accent.main : 'transparent'};
  border: ${props => props.$selected ? 'none' : `1px solid ${props.theme.colors.neutral.light}`};
  border-radius: 50%;
  width: 60px;
  height: 60px;
  padding: 0;
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  flex-shrink: 0;
  
  &:hover {
    background: ${props => props.$selected ? props.theme.colors.accent.main : props.theme.colors.neutral.light};
    border-color: ${props => props.$selected ? 'transparent' : props.theme.colors.neutral.medium};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 48px;
    height: 48px;
    gap: 2px;
    overflow: hidden;
  }
`;

const DayText = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${props => props.$selected ? props.theme.colors.primary.background : props.theme.colors.primary.text};
  opacity: ${props => props.$selected ? 1 : 0.6};
  line-height: 1;
`;

const DateText = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.$selected ? props.theme.colors.primary.background : props.theme.colors.primary.text};
  line-height: 1;
`;

const ClassList = styled(motion.div)`
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.layoutGutter};
  max-width: 1200px;
  margin: 0 auto;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  color: ${props => props.theme.colors.accent.main};
`;

const NoClassesMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.md};
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  color: ${props => props.theme.colors.primary.text};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const EmptyMessage = styled.p`
  margin: 0;
  font-size: ${props => props.theme.typography.secondary.body.fontSize};
  opacity: 0.7;
`;

const FindNextButton = styled.button`
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: ${props => props.theme.typography.button.fontSize};
  font-weight: ${props => props.theme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme.typography.button.letterSpacing};
  text-transform: ${props => props.theme.typography.button.textTransform};
  
  background: transparent;
  color: ${props => props.theme.colors.primary.buttons};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  text-decoration: none;
  
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &::after {
    content: '→';
    font-size: 1.2em;
  }
`;

const ClassCard = styled(motion.div)`
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.primary.background};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.accent.bright};
  transition: ${props => props.theme.transitions.default};
  opacity: ${props => props.$isPast ? 0.5 : 1};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    transform: ${props => props.$isPast ? 'none' : 'translateY(-2px)'};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const TimeColumn = styled.div`
  min-width: 70px;
  margin-right: ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    min-width: 55px;
    margin-right: ${props => props.theme.spacing.sm};
  }
`;

const Time = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary.text};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1rem;
    font-weight: 600;
  }
`;

const InstructorImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 40px;
    height: 40px;
    margin-right: ${props => props.theme.spacing.sm};
  }
`;

const ClassDetails = styled.div`
  flex: 1;
`;

const ClassName = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const InstructorName = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.7;
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ClassMeta = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.6;
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 25px;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: 0.85rem;
  font-weight: ${props => props.theme.typography.button.fontWeight};
  color: ${props => props.theme.colors.primary.buttons};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  white-space: nowrap;
  margin-left: ${props => props.theme.spacing.xs};
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: ${props => props.theme.colors.neutral.medium};
    color: ${props => props.theme.colors.neutral.medium};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 0.75rem;
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    margin-left: ${props => props.theme.spacing.xs};
  }
`;


const BookingPageContent = ({ classType }) => {
  // State
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true); // Start loading until location is set
  const [error, setError] = useState(null);
  
  // Popup state
  const [reservationPopupOpen, setReservationPopupOpen] = useState(false);
  const [waitlistPopupOpen, setWaitlistPopupOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekOffset, setWeekOffset] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Default filters
  const getDefaultFilters = () => {
    const baseFilters = {
      location: '',  // Will be set after locations are loaded
      hours: ['morning', 'afternoon', 'evening'],  // All time options selected by default
      instructors: [],
      hideWaitlist: false
    };

    // Always default to empty array - means show all classes of this type
    return { ...baseFilters, classTypes: [] };
  };

  const [filters, setFilters] = useState(getDefaultFilters());
  const [defaultLocationId, setDefaultLocationId] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasUpcomingClasses, setHasUpcomingClasses] = useState(null);
  const [isSearchingNext, setIsSearchingNext] = useState(false);

  // Load locations and set default on mount
  useEffect(() => {
    const loadDefaultLocation = async () => {
      try {
        const locations = await locationService.getLocations();
        if (locations && locations.length > 0) {
          // Sort alphabetically and get the first one
          const sortedLocations = locations.sort((a, b) => a.name.localeCompare(b.name));
          const firstLocation = sortedLocations[0];
          
          // Update filters with the first location
          setFilters(prevFilters => ({
            ...prevFilters,
            location: firstLocation.id
          }));
          
          // Store the default location ID
          setDefaultLocationId(firstLocation.id);
        }
      } catch (error) {
        console.error('Failed to load default location:', error);
        // Set a fallback location if API fails
        setFilters(prevFilters => ({
          ...prevFilters,
          location: 'fallback-location'
        }));
      } finally {
        // Mark as initialized even if location loading fails
        setIsInitialized(true);
      }
    };

    loadDefaultLocation();
  }, []); // Only run once on mount

  // Function to find next available class/event
  const findNextAvailableClass = async () => {
    try {
      setIsSearchingNext(true);
      
      // Quick search through next 14 days
      for (let i = 1; i <= 14; i++) {
        const checkDate = new Date(selectedDate);
        checkDate.setDate(checkDate.getDate() + i);
        
        const upcomingFilters = {
          date: formatDateForAPI(checkDate), // Use local timezone formatting
          locationId: filters.location
        };
        
        const upcomingData = await classService.getClasses(upcomingFilters);
        
        // Apply same filtering logic
        let upcomingFiltered = upcomingData || [];
        
        const eventTypeMap = {
          'group': 'group',
          'private': 'private', 
          'events': 'event'
        };
        
        const targetEventType = eventTypeMap[classType];
        upcomingFiltered = upcomingFiltered.filter(cls => 
          cls.category?.event_type === targetEventType
        );
        
        // Apply other filters
        if (filters.classTypes && filters.classTypes.length > 0) {
          upcomingFiltered = upcomingFiltered.filter(cls => 
            filters.classTypes.includes(cls.category?.id)
          );
        }
        
        if (filters.instructors && filters.instructors.length > 0) {
          upcomingFiltered = upcomingFiltered.filter(cls => 
            filters.instructors.includes(cls.instructor?.id)
          );
        }
        
        if (filters.hours && filters.hours.length > 0) {
          upcomingFiltered = upcomingFiltered.filter(cls => {
            const startTime = new Date(cls.start_time);
            const hour = startTime.getHours();
            
            return filters.hours.some(timeId => {
              switch (timeId) {
                case 'morning':
                  return hour >= 6 && hour < 12;
                case 'afternoon':
                  return hour >= 12 && hour < 17;
                case 'evening':
                  return hour >= 17 && hour < 22;
                default:
                  return false;
              }
            });
          });
        }
        
        if (upcomingFiltered.length > 0) {
          // Found classes on this date!
          setSelectedDate(checkDate);
          setIsSearchingNext(false);
          return;
        }
      }
      
      // No classes found in next 14 days
      setHasUpcomingClasses(false);
      setIsSearchingNext(false);
    } catch (error) {
      console.error('Error finding next class:', error);
      setIsSearchingNext(false);
    }
  };

  // Load classes from real API
  useEffect(() => {
    const loadClasses = async () => {
      // Don't load until initialized with a location
      if (!isInitialized || !filters.location) {
        // If we're initialized but no location, stop loading
        if (isInitialized && !filters.location) {
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Build API filters
        const apiFilters = {
          date: formatDateForAPI(selectedDate), // Use local timezone formatting
          locationId: filters.location  // Always include location since we check for it above
        };

        const classData = await classService.getClasses(apiFilters);
        
        // Filter by class type on client side if needed
        let filteredClasses = classData || [];
        
        // Filter by event type first (based on the page we're on)
        const eventTypeMap = {
          'group': 'group',
          'private': 'private', 
          'events': 'event'
        };
        
        const targetEventType = eventTypeMap[classType];
        filteredClasses = filteredClasses.filter(cls => 
          cls.category?.event_type === targetEventType
        );
        
        // Apply additional client-side filters
        // If classTypes is empty, show all classes of the target event type
        if (filters.classTypes && filters.classTypes.length > 0) {
          filteredClasses = filteredClasses.filter(cls => 
            filters.classTypes.includes(cls.category?.id)
          );
        }

        if (filters.instructors && filters.instructors.length > 0) {
          filteredClasses = filteredClasses.filter(cls => 
            filters.instructors.includes(cls.instructor?.id)
          );
        }

        // Apply time-of-day filtering
        if (filters.hours && filters.hours.length > 0) {
          // Filter classes based on selected time periods
          filteredClasses = filteredClasses.filter(cls => {
            const startTime = new Date(cls.start_time);
            const hour = startTime.getHours();
            
            return filters.hours.some(timeId => {
              switch (timeId) {
                case 'morning':
                  return hour >= 6 && hour < 12;  // 6 AM - 12 PM
                case 'afternoon':
                  return hour >= 12 && hour < 17; // 12 PM - 5 PM
                case 'evening':
                  return hour >= 17 && hour < 22; // 5 PM - 10 PM
                default:
                  return false;
              }
            });
          });
        } else if (filters.hours && filters.hours.length === 0) {
          // No time periods selected = show no classes
          filteredClasses = [];
        }

        if (filters.hideWaitlist) {
          filteredClasses = filteredClasses.filter(cls => 
            cls.status !== 'full'
          );
        }

        setClasses(filteredClasses);
        
        // Reset upcoming classes state when we load new data
        setHasUpcomingClasses(null);
      } catch (err) {
        console.error('Failed to load classes:', err);
        setError('Failed to load classes. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadClasses();
  }, [selectedDate, filters, classType, isInitialized]);

  // Helper functions
  const formatDateForAPI = (date) => {
    // Format date as YYYY-MM-DD in local timezone to avoid timezone conversion issues
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const generateWeekDates = (offset) => {
    const dates = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + (offset * 7));
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const currentWeekDates = generateWeekDates(weekOffset);

  const formatDay = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  };

  const formatDate = (date) => {
    return date.getDate();
  };

  const formatTime = (timeString) => {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const isSameDay = (date1, date2) => {
    // Compare dates using local timezone to avoid timezone conversion issues
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const isPastClass = (classData) => {
    const classEndTime = new Date(classData.end_time);
    const now = new Date();
    return classEndTime < now;
  };

  const isTodaySelected = () => {
    const today = new Date();
    return isSameDay(selectedDate, today);
  };

  const returnToToday = () => {
    const today = new Date();
    const currentDate = selectedDate;
    if (today > currentDate) {
      setDirection(1);
    } else if (today < currentDate) {
      setDirection(-1);
    } else {
      setDirection(0);
    }
    setSelectedDate(today);
    setWeekOffset(0);
  };

  const getCurrentMonthYear = () => {
    const firstDateOfWeek = currentWeekDates[0];
    return firstDateOfWeek.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getInstructorImage = (instructorName) => {
    // Match instructor by name and use their photo from the instructor registry
    const instructor = instructors.find(inst => inst.name === instructorName);
    if (instructor && instructor.photo) {
      return instructor.photo;
    }
    
    // Fallback to grey user icon if no match found
    return 'data:image/svg+xml;base64,' + btoa(`
      <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="25" cy="25" r="25" fill="#E5E7EB"/>
        <circle cx="25" cy="20" r="8" fill="#9CA3AF"/>
        <path d="M10 40c0-8.284 6.716-15 15-15s15 6.716 15 15" fill="#9CA3AF"/>
      </svg>
    `);
  };

  const getClassAvailability = (classData) => {
    if (classData.status === 'full') {
      return 'waitlist';
    }
    return 'available';
  };

  const handleReservation = (classData) => {
    setSelectedClass(classData);
    
    const availability = getClassAvailability(classData);
    if (availability === 'available') {
      setReservationPopupOpen(true);
    } else if (availability === 'waitlist') {
      setWaitlistPopupOpen(true);
    }
  };

  const handleReservationConfirm = async (classData, paymentMethod) => {
    try {
      const result = await classService.bookClass(classData.id, paymentMethod);
      console.log('Booking result:', result);
      
      // Refresh classes after booking
      const apiFilters = {
        date: formatDateForAPI(selectedDate), // Use local timezone formatting
      };
      const updatedClasses = await classService.getClasses(apiFilters);
      setClasses(updatedClasses || []);
      
    } catch (error) {
      console.error('Booking failed:', error);
      // Handle error (show error message to user)
    }
  };

  const handleWaitlistConfirm = (classData) => {
    console.log(`Adding to waitlist for ${classType} class:`, classData);
    // TODO: Implement waitlist API call
  };

  const getButtonText = (availability) => {
    switch (availability) {
      case 'available':
        return 'Reserve';
      case 'waitlist':
        return 'Waitlist Only';
      default:
        return 'Reserve';
    }
  };

  const hasActiveFilters = () => {
    const defaultFilters = getDefaultFilters();
    
    // Only show * if filters differ from defaults
    // Use the dynamically loaded default location ID
    const effectiveDefaultLocation = defaultLocationId || defaultFilters.location;
    
    return filters.location !== effectiveDefaultLocation || 
           JSON.stringify(filters.hours.sort()) !== JSON.stringify(defaultFilters.hours.sort()) ||
           filters.instructors.length > 0 || 
           filters.hideWaitlist;
  };

  const handleFilterClick = () => {
    setIsFilterOpen(true);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const [direction, setDirection] = useState(0);

  const handleDateSelect = (date) => {
    const currentDate = selectedDate;
    if (date > currentDate) {
      setDirection(1);
    } else if (date < currentDate) {
      setDirection(-1);
    } else {
      setDirection(0);
    }
    setSelectedDate(date);
  };

  const handlePrevWeek = () => {
    setDirection(-1);
    setWeekOffset(weekOffset - 1);
  };

  const handleNextWeek = () => {
    setDirection(1);
    setWeekOffset(weekOffset + 1);
  };

  return (
    <Container>
      <CalendarContainer>
        <CalendarNavigation>
          <CalendarNavButton 
            onClick={handlePrevWeek}
          >
            ‹
          </CalendarNavButton>
          
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, overflow: 'hidden', margin: '0 4px' }}>
            <CalendarHeaderContainer>
              <ReturnToTodayButton 
                $show={!isTodaySelected()}
                onClick={returnToToday}
              >
                ← Today
              </ReturnToTodayButton>
              <MonthDisplay>
                {getCurrentMonthYear()}
              </MonthDisplay>
              <FilterButton onClick={handleFilterClick} $hasActiveFilters={hasActiveFilters()}>
                <FilterIcon />
              </FilterButton>
            </CalendarHeaderContainer>
            
            <AnimatePresence mode="wait">
              <CalendarScroll
                key={weekOffset}
                initial={{ opacity: 0, x: direction > 0 ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction > 0 ? -20 : 20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {currentWeekDates.map((date, index) => (
                  <DateOption
                    key={index}
                    $selected={isSameDay(date, selectedDate)}
                    onClick={() => handleDateSelect(date)}
                  >
                    <DayText $selected={isSameDay(date, selectedDate)}>
                      {formatDay(date)}
                    </DayText>
                    <DateText $selected={isSameDay(date, selectedDate)}>
                      {formatDate(date)}
                    </DateText>
                  </DateOption>
                ))}
              </CalendarScroll>
            </AnimatePresence>
          </div>
          
          <CalendarNavButton onClick={handleNextWeek}>
            ›
          </CalendarNavButton>
        </CalendarNavigation>
      </CalendarContainer>

      <AnimatePresence mode="wait">
        <ClassList
          key={`${selectedDate.toDateString()}-${JSON.stringify(filters)}`}
          initial={{ opacity: 0, x: direction > 0 ? 20 : -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction > 0 ? -20 : 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {loading && (
            <LoadingMessage>Loading classes...</LoadingMessage>
          )}
          
          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}
          
          {!loading && !error && classes.length === 0 && (
            <NoClassesMessage>
              <EmptyMessage>
                {hasUpcomingClasses === false 
                  ? `No upcoming ${classType === 'events' ? 'events' : 'classes'}, please check in later!`
                  : `No ${classType === 'events' ? 'events' : 'classes'} today`
                }
              </EmptyMessage>
              {hasUpcomingClasses !== false && (
                <FindNextButton 
                  onClick={findNextAvailableClass}
                  disabled={isSearchingNext}
                >
                  {isSearchingNext 
                    ? 'Searching...' 
                    : `Find Next ${classType === 'events' ? 'Event' : 'Class'}`
                  }
                </FindNextButton>
              )}
            </NoClassesMessage>
          )}
          
          {!loading && !error && classes.length > 0 && classes.map((classData) => {
            const isPast = isPastClass(classData);
            return (
              <ClassCard key={classData.id} $isPast={isPast}>
                <TimeColumn>
                  <Time>{formatTime(classData.start_time)}</Time>
                </TimeColumn>

                <InstructorImage 
                  src={getInstructorImage(classData.instructor?.name)}
                  alt={classData.instructor?.name || 'Instructor'}
                />

                <ClassDetails>
                  <ClassName>
                    {classData.name} [{classData.difficulty_level || 'All Levels'}]
                  </ClassName>
                  <InstructorName>
                    {classData.instructor?.name || 'TBA'}
                  </InstructorName>
                  <ClassMeta>
                    {classData.location?.name || 'TBA'} • {Math.round((new Date(classData.end_time) - new Date(classData.start_time)) / (1000 * 60))} min
                  </ClassMeta>
                </ClassDetails>

                <ActionButton 
                  onClick={() => handleReservation(classData)}
                  disabled={isPast}
                >
                  {isPast ? 'Past' : getButtonText(getClassAvailability(classData))}
                </ActionButton>
              </ClassCard>
            );
          })}
        </ClassList>
      </AnimatePresence>
      
      <FilterPopup
        isOpen={isFilterOpen}
        onClose={handleCloseFilter}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        classType={classType}
      />

      <ReservationPopup
        isOpen={reservationPopupOpen}
        onClose={() => setReservationPopupOpen(false)}
        classData={selectedClass}
        onConfirm={handleReservationConfirm}
      />

      <WaitlistConfirmationPopup
        isOpen={waitlistPopupOpen}
        onClose={() => setWaitlistPopupOpen(false)}
        classData={selectedClass}
        onConfirm={handleWaitlistConfirm}
      />
    </Container>
  );
};

export default BookingPageContent;